import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Heart, Crown } from 'lucide-react';
import { useFavorites } from '../hooks/useFavorites';
import { useAuth } from '../contexts/AuthContext';
import LoginPromptModal from './LoginPromptModal';
import PremiumUpgradeModal from './PremiumUpgradeModal';

export interface CategoryType {
  _id: string;
  name: string;
  slug: string;
  icon: string;
  bgColor: string;
  iconColor: string;
}

export interface AgeGroupType {
  _id: string;
  name: string;
  slug: string;
  order: number;
}

export interface GameType {
  _id: string;
  name: string;
  slug: string;
  image: string;
  path: string;
  premium: boolean;
  categories: CategoryType[];
  ageGroups: AgeGroupType[];
  isActive: boolean;
  order: number;
  isNew: boolean;
  createdAt: string;
}

/* ── Styled ── */

const Card = styled.div`
  background: white;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.06);
  border: 1px solid #f1f5f9;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
  display: flex;
  flex-direction: column;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.1);
  }
`;

const ImgWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 75%; /* 4:3 */
  overflow: hidden;
  img {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    object-fit: cover;
  }
`;

const NewBadge = styled.div`
  position: absolute;
  top: 8px; left: 8px;
  background: #FEA92D;
  color: white;
  padding: 3px 9px;
  border-radius: 9999px;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  z-index: 2;
`;

const PremiumBadge = styled.div`
  position: absolute;
  top: 8px; right: 42px;
  background: #facc15;
  color: white;
  padding: 3px 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 9px;
  font-weight: 800;
  z-index: 2;
  svg {
    width: 12px;
    height: 12px;
  }
`;

const HeartBtn = styled.button<{ $active?: boolean }>`
  position: absolute;
  top: 8px; right: 8px;
  width: 30px; height: 30px;
  border-radius: 50%;
  background: rgba(255,255,255,0.92);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 3;
  box-shadow: 0 2px 6px rgba(0,0,0,0.12);
  color: #ef4444;
  svg {
    width: 17px;
    height: 17px;
  }
`;

const Body = styled.div`
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const GameName = styled.h3`
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
  min-height: 2.8em;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

const CategoryPill = styled.span<{ bg: string; color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 3px 8px;
  border-radius: 9999px;
  background: ${p => p.bg};
  color: ${p => p.color};
  font-family: 'Plus Jakarta Sans', sans-serif;
  .material-symbols-outlined { font-size: 11px; }
`;

const AgePill = styled.span`
  display: inline-block;
  font-size: 9px;
  font-weight: 600;
  padding: 3px 7px;
  border-radius: 9999px;
  background: #f1f5f9;
  color: #64748b;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

/* ── Component ── */

interface Props {
  game: GameType;
}

export default function GameCard({ game }: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [showModal, setShowModal] = React.useState(false);
  const [showPremiumModal, setShowPremiumModal] = React.useState(false);
  const fav = isFavorite(game.slug);
  
  // Check if user has active premium
  const isPremium = user?.subscription?.status === 'active' && 
    user?.subscription?.endDate && 
    new Date(user.subscription.endDate) > new Date();

  const handleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const result = await toggleFavorite(game.slug);
    if (result === null) setShowModal(true);
  };
  
  const handleCardClick = () => {
    // If game is premium and user is not premium, show upgrade modal
    if (game.premium && !isPremium) {
      setShowPremiumModal(true);
      return;
    }
    navigate(game.path);
  };

  return (
    <>
      <Card onClick={handleCardClick}>
        <ImgWrapper>
          <img src={game.image} alt={game.name} />
          {game.isNew && <NewBadge>NEW</NewBadge>}
          {game.premium && (
            <PremiumBadge>
              <Crown fill="currentColor" />
              PREMIUM
            </PremiumBadge>
          )}
          <HeartBtn onClick={handleFavorite} $active={fav}>
            <Heart fill={fav ? 'currentColor' : 'none'} strokeWidth={fav ? 0 : 2} />
          </HeartBtn>
        </ImgWrapper>

        <Body>
          <GameName>{game.name}</GameName>
          <MetaRow>
            {game.categories.slice(0, 2).map(cat => (
              <CategoryPill key={cat._id} bg={cat.bgColor} color={cat.iconColor}>
                <span className="material-symbols-outlined">{cat.icon}</span>
                {cat.name}
              </CategoryPill>
            ))}
          </MetaRow>
          <MetaRow>
            {game.ageGroups.slice(0, 3).map(ag => (
              <AgePill key={ag._id}>{ag.name}</AgePill>
            ))}
          </MetaRow>
        </Body>
      </Card>

      {showModal && (
        <LoginPromptModal
          onClose={() => setShowModal(false)}
        />
      )}
      
      {showPremiumModal && (
        <PremiumUpgradeModal
          isOpen={showPremiumModal}
          onClose={() => setShowPremiumModal(false)}
          featureName={game.name}
        />
      )}
    </>
  );
}
